import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    updateCartRecommendations();
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm =
      event.detail.data?.source === "product-form-component";

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true,
  ) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle("hidden", itemCount === 0);
    this.refs.cartBubble.classList.toggle("visually-hidden", itemCount === 0);
    this.refs.cartBubble.classList.toggle(
      "cart-bubble--animating",
      itemCount > 0 && animate,
    );

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);

    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      }),
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem("cart-count");
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null)
      return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}

/**
 * Refreshes cart drawer recommendations after cart morphs or when line items change.
 */
function onDocumentCartUpdateRecommendations() {
  updateCartRecommendations();
}

/**
 * Renders recommendations title and optional recommendation markup into the container.
 * Keeping the title inside this container prevents parent flex gaps from separating it.
 * @param {HTMLElement} container
 * @param {string} [html]
 */
function renderRecommendationsContainer(container, html = "") {
  const titleText =
    container.getAttribute("data-recommendation-title") || "You might like also:";
  const title = document.createElement("h5");
  title.id = "cart-recommendations-title";
  title.textContent = titleText;

  if (!html) {
    container.replaceChildren(title);
    return;
  }

  const fragmentTemplate = document.createElement("template");
  fragmentTemplate.innerHTML = html;
  container.replaceChildren(title, fragmentTemplate.content.cloneNode(true));
}

async function updateCartRecommendations() {
  // The cart-items section is morphed in response to the same `cart:update`
  // event we're reacting to. When the cart transitions from empty to
  // non-empty, our listener fires before the morph adds the recommendations
  // container to the DOM (the empty-cart server render omits it entirely —
  // see `snippets/cart-drawer.liquid`). Wait one animation frame so the
  // sibling listener has a chance to complete the morph, then try again.
  /** @type {HTMLElement | null} */
  let container = document.querySelector("#cart-recommendations-container");
  if (!container) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    container = document.querySelector("#cart-recommendations-container");
    if (!container) return;
  }

  const maxProductsRaw = parseInt(
    container.getAttribute("data-recommendation_max_products") || "10",
    10,
  );
  const maxProducts = Number.isNaN(maxProductsRaw) ? 10 : maxProductsRaw;
  const recommendationType =
    container.getAttribute("data-recommendation_type") || "related";
  const complementaryFallbackEnabled =
    container.getAttribute("data-complementary-fallback-enabled") === "true";
  const routesRoot =
    /** @type {any} */ (window.Shopify)?.routes?.root || "/";

  // If complementary mode has no selected fallback collection, keep recommendations empty.
  if (recommendationType === "complementary" && !complementaryFallbackEnabled) {
    renderRecommendationsContainer(container);
    return;
  }

  try {
    // 1. Get the first item ID from the Shopify Cart API.
    const cartResponse = await fetch(routesRoot + "cart.js");
    if (!cartResponse.ok) {
      throw new Error(`Cart request failed with status ${cartResponse.status}`);
    }

    /** @type {{ item_count?: number, items?: Array<{ product_id?: number }> }} */
    const cart = await cartResponse.json();
    if (!cart?.item_count || !Array.isArray(cart.items) || !cart.items.length) {
      renderRecommendationsContainer(container);
      return;
    }

    const firstProductId = cart.items[0]?.product_id;
    if (!firstProductId) {
      renderRecommendationsContainer(container);
      return;
    }

    const sectionId = "cart-drawer-recommendations";
    const recommendationsUrl = `${routesRoot}recommendations/products?section_id=${sectionId}&product_id=${firstProductId}&limit=${maxProducts}&intent=${recommendationType}`;

    // 2. Fetch rendered recommendations HTML for the first product.
    const recommendationsResponse = await fetch(recommendationsUrl);
    if (!recommendationsResponse.ok) {
      throw new Error(
        `Recommendations request failed with status ${recommendationsResponse.status}`,
      );
    }

    const html = await recommendationsResponse.text();
    renderRecommendationsContainer(container, html);
  } catch (error) {
    // Avoid breaking cart interactions when recommendations are unavailable.
    console.error("Cart recommendations error:", error);
    renderRecommendationsContainer(container);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, onDocumentCartUpdateRecommendations);
